import { matchesItemSearch } from "../../UI/Utils/matchesItemSearch.ts";
import {
  isConcludedSale,
  refundedAmountOf,
  SaleCustomer,
  SaleRecord,
  sumOf,
} from "./SaleRecord.ts";

export type CustomerOrders = {
  customer: SaleCustomer;
  orders: SaleRecord[];
};

export function customerKeyOf(customer: SaleCustomer): string {
  return customer.email.trim().toLowerCase();
}

export function fullNameOf(customer: SaleCustomer): string {
  return `${customer.firstName} ${customer.lastName}`.trim();
}

export function newestOrdersFirst(orders: SaleRecord[]): SaleRecord[] {
  return [...orders].sort((first, second) =>
    second.orderedAtUtc.localeCompare(first.orderedAtUtc),
  );
}

export function ordersByCustomer(orders: SaleRecord[]): CustomerOrders[] {
  const customers = new Map<string, CustomerOrders>();
  for (const order of newestOrdersFirst(orders)) {
    const customerKey = customerKeyOf(order.customer);
    const knownCustomer = customers.get(customerKey);
    customers.set(customerKey, {
      customer: knownCustomer?.customer ?? order.customer,
      orders: [...(knownCustomer?.orders ?? []), order],
    });
  }
  return [...customers.values()];
}

export function numberOfOrdersOf({ orders }: CustomerOrders): number {
  return orders.length;
}

export function soldToCustomerInCents({ orders }: CustomerOrders): number {
  return sumOf(
    orders.filter(isConcludedSale),
    (order) => order.amountDueInCents,
  );
}

export function refundedToCustomerInCents({ orders }: CustomerOrders): number {
  return sumOf(orders, refundedAmountOf);
}

export function couponDiscountOfCustomerInCents({
  orders,
}: CustomerOrders): number {
  return sumOf(orders, (order) => order.couponDiscountInCents);
}

export function matchesCustomerSearch(
  customer: SaleCustomer,
  search: string,
): boolean {
  return matchesItemSearch(search, [
    customer.firstName,
    customer.lastName,
    customer.email,
  ]);
}
